import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ConfirmBox from "../ConfirmBox";
import { appThemes } from "../../MeetingAppContextDef";
import { useTranslation } from "react-i18next";
import "./SettingDialogueBox.css";

const AudioAnalyser = ({ audioTrack }) => {
  const audioTrackRef = useRef();
  const audioAnalyserIntervalRef = useRef();

  const [volume, setVolume] = useState(null);

  const analyseAudio = (audioTrack) => {
    const audioStream = new MediaStream([audioTrack]);
    const audioContext = new AudioContext();

    const audioSource = audioContext.createMediaStreamSource(audioStream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 512;
    analyser.minDecibels = -127;
    analyser.maxDecibels = 0;
    analyser.smoothingTimeConstant = 0.4;

    audioSource.connect(analyser);

    const volumes = new Uint8Array(analyser.frequencyBinCount);
    const volumeCallback = () => {
      analyser.getByteFrequencyData(volumes);

      const volumeSum = volumes.reduce((sum, vol) => sum + vol);
      const averageVolume = volumeSum / volumes.length;

      setVolume(averageVolume);
    };

    audioAnalyserIntervalRef.current = setInterval(volumeCallback, 100);
  };

  const stopAudioAnalyse = () => {
    clearInterval(audioAnalyserIntervalRef.current);
  };

  useEffect(() => {
    audioTrackRef.current = audioTrack;

    if (audioTrack) {
      analyseAudio(audioTrack);
    } else {
      stopAudioAnalyse();
    }
  }, [audioTrack]);

  const barHeights = [40, 70, 100, 100, 70, 40];

  return (
    <div className="audio-analyser">
      <div className="audio-analyser-half top">
        {barHeights.map((height, j) => (
          <div
            key={`audio_bar_top_h${height}_p${j}`}
            className="audio-bar top"
            style={{
              height: `${(volume / 256) * height}%`,
            }}
          />
        ))}
      </div>
      <div className="audio-analyser-half bottom">
        {barHeights.map((height, j) => (
          <div
            key={`audio_bar_bottom_h${height}_p${j}`}
            className="audio-bar bottom"
            style={{
              height: `${(volume / 256) * height}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

AudioAnalyser.propTypes = {
  audioTrack: PropTypes.shape({
    getSettings: PropTypes.func,
  }),
};

export default function SettingDialogueBox({
  open,
  onClose,
  popupVideoPlayerRef,
  webcams,
  mics,
  setting,
  setSetting,
  setSelectedMic,
  setSelectedWebcam,
  changeWebcam,
  changeMic,
  videoTrack,
  audioTrack,
  participantCanToggleSelfMic,
  participantCanToggleSelfWebcam,
  appTheme,
}) {
  const { t } = useTranslation();
  const [dlgDevices, setDlgDevices] = useState(false);
  const [micDropdownOpen, setMicDropdownOpen] = useState(false);
  const [webcamDropdownOpen, setWebcamDropdownOpen] = useState(false);
  const micSelectRef = useRef(null);
  const webcamSelectRef = useRef(null);

  const getThemeClass = () => {
    if (appTheme === appThemes.LIGHT) return 'light-theme';
    if (appTheme === appThemes.DARK) return 'dark-theme';
    return '';
  };

  const handleMicChange = (deviceId) => {
    changeMic(deviceId);
    setSelectedMic((s) => ({ ...s, id: deviceId }));
    setMicDropdownOpen(false);
  };

  const handleWebcamChange = (deviceId) => {
    changeWebcam(deviceId);
    setSelectedWebcam((s) => ({ ...s, id: deviceId }));
    setWebcamDropdownOpen(false);
  };

  const currentMicId = audioTrack?.getSettings()?.deviceId || '';
  const currentWebcamId = videoTrack?.getSettings()?.deviceId || '';

  const selectedMicLabel = () => {
    const selectedMic = mics?.find(m => m.deviceId === currentMicId);
    if (selectedMic?.label) return selectedMic.label;
    if (currentMicId) return "Select Microphone";
    return "Mic not selected";
  };

  const selectedWebcamLabel = () => {
    const selectedWebcam = webcams?.find(w => w.deviceId === currentWebcamId);
    if (selectedWebcam?.label) return selectedWebcam.label;
    if (currentWebcamId) return "Select Webcam";
    return "Webcam not selected";
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (micSelectRef.current && !micSelectRef.current.contains(e.target)) {
        setMicDropdownOpen(false);
      }
      if (webcamSelectRef.current && !webcamSelectRef.current.contains(e.target)) {
        setWebcamDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="setting-dialogue-container">
      {/* Microphone Select */}
      <div className="device-select-wrapper" ref={micSelectRef}>
        <label htmlFor="mic-select" className={`device-label ${getThemeClass()}`}>
          Microphone
        </label>
        <div className="custom-select">
          <button
            type="button"
            id="mic-select"
            className={`custom-select-trigger ${getThemeClass()} ${micDropdownOpen ? 'open' : ''}`}
            onClick={() => setMicDropdownOpen(!micDropdownOpen)}
            onKeyDown={(e) => handleKeyDown(e, () => setMicDropdownOpen(!micDropdownOpen))}
            aria-label="Select microphone"
            aria-expanded={micDropdownOpen}
          >
            <span className="custom-select-text">{selectedMicLabel()}</span>
            <svg className="custom-select-arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5z" fill="currentColor" />
            </svg>
          </button>
          {micDropdownOpen && (
            <div className={`custom-select-options ${getThemeClass()}`}>
              {mics?.map((item, i) => {
                return item?.kind === "audioinput" ? (
                  <button
                    type="button"
                    key={item.deviceId || i}
                    className={`custom-select-option ${getThemeClass()} ${
                      item.deviceId === currentMicId ? 'selected' : ''
                    }`}
                    onClick={() => handleMicChange(item.deviceId)}
                    onKeyDown={(e) => handleKeyDown(e, () => handleMicChange(item.deviceId))}
                    aria-current={item.deviceId === currentMicId ? 'true' : 'false'}
                  >
                    {item?.label ? item.label : `Mic ${i + 1}`}
                  </button>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>

      {/* Webcam Select */}
      <div className="device-select-wrapper" ref={webcamSelectRef}>
        <label htmlFor="webcam-select" className={`device-label ${getThemeClass()}`}>
          Webcam
        </label>
        <div className="custom-select">
          <button
            type="button"
            id="webcam-select"
            className={`custom-select-trigger ${getThemeClass()} ${webcamDropdownOpen ? 'open' : ''}`}
            onClick={() => setWebcamDropdownOpen(!webcamDropdownOpen)}
            onKeyDown={(e) => handleKeyDown(e, () => setWebcamDropdownOpen(!webcamDropdownOpen))}
            aria-label="Select webcam"
            aria-expanded={webcamDropdownOpen}
          >
            <span className="custom-select-text">{selectedWebcamLabel()}</span>
            <svg className="custom-select-arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5z" fill="currentColor" />
            </svg>
          </button>
          {webcamDropdownOpen && (
            <div className={`custom-select-options ${getThemeClass()}`}>
              {webcams?.map((item, i) => {
                return item?.kind === "videoinput" ? (
                  <button
                    type="button"
                    key={item.deviceId || i}
                    className={`custom-select-option ${getThemeClass()} ${
                      item.deviceId === currentWebcamId ? 'selected' : ''
                    }`}
                    onClick={() => handleWebcamChange(item.deviceId)}
                    onKeyDown={(e) => handleKeyDown(e, () => handleWebcamChange(item.deviceId))}
                    aria-current={item.deviceId === currentWebcamId ? 'true' : 'false'}
                  >
                    {item?.label ? item.label : `Webcam ${i + 1}`}
                  </button>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmBox
        open={dlgDevices}
        title="Mic or webcam not available"
        subTitle="Please connect a mic and webcam to speak and share your video in the meeting. You can also join without them."
        successText="DISMISS"
        onSuccess={() => {
          setDlgDevices(false);
        }}
      />
    </div>
  );
}

SettingDialogueBox.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  popupVideoPlayerRef: PropTypes.object,
  webcams: PropTypes.arrayOf(
    PropTypes.shape({
      deviceId: PropTypes.string,
      kind: PropTypes.string,
      label: PropTypes.string,
      find: PropTypes.func,
    })
  ),
  mics: PropTypes.arrayOf(
    PropTypes.shape({
      deviceId: PropTypes.string,
      kind: PropTypes.string,
      label: PropTypes.string,
      find: PropTypes.func,
    })
  ),
  setting: PropTypes.object,
  setSetting: PropTypes.func,
  setSelectedMic: PropTypes.func,
  setSelectedWebcam: PropTypes.func,
  changeWebcam: PropTypes.func,
  changeMic: PropTypes.func,
  videoTrack: PropTypes.shape({
    getSettings: PropTypes.func,
  }),
  audioTrack: PropTypes.shape({
    getSettings: PropTypes.func,
  }),
  participantCanToggleSelfMic: PropTypes.bool,
  participantCanToggleSelfWebcam: PropTypes.bool,
  appTheme: PropTypes.string,
};
